import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useReducer,
} from "react";
import { SettingsContext } from "./SettingsContext";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Checkbox from "@amzn/meridian/checkbox";
import TimePicker from "@amzn/meridian/time-picker";
import { DEFAULT_CYCLE, induct } from "./SettingsConstant";
import {
  add,
  differenceInMinutes,
  format,
  isAfter,
  isEqual,
  isValid,
} from "date-fns";
import { getStationCode } from "../../network/NetworkUtil";
import { updateTriggerConfiguration } from "../../handler/ParentDataHandler";

const TIME_FORMAT = "HH:mm:ss";
const POST_TIME_ERROR = "Post time must be different than Induct time";
const POST_TIME_KEY = "postTime";
const RUN_TIME_KEY = "autoRun";

const AutoProcessesReducer = (state, action) => {
  switch (action.type) {
    case "SET_DATA":
      return {
        ...state,
        oldData: action.payload.data,
        dirty: false,
        data: action.payload.data,
      };
    case "DELETE_ITEM":
      return {
        ...state,
        dirty: true,
        data: state.data.filter((item) => item.id !== action.payload.id),
      };
    case "CHANGE_ITEM":
      const temporary = {
        ...state,
        data: state.data.map((item) =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item,
        ),
      };
      return {
        ...temporary,
        dirty: !(
          // change this when we have more than one entry (IE not using only CYCLE_1)
          // to use something like lodash.deepEquals.
          (JSON.stringify(temporary.data) === JSON.stringify(state.oldData))
        ),
      };
    default:
      return state;
  }
};

const ShiftPlanAutomation = (props) => {
  const { viewModel, dispatch } = useContext(SettingsContext);

  const isUserAuthorisedForSettingsEdit = useMemo(
    () => props.props.isUserAuthorisedForSettingsEdit,
    [props],
  );

  const parseDataFromBackend = useCallback(() => {
    const timeZone = viewModel.opsClockData.nodeTimeZone
      ? viewModel.opsClockData.nodeTimeZone
      : Intl.DateTimeFormat().resolvedOptions().timeZone;
    const entries = [];
    if (viewModel.opsClockData && viewModel.opsClockData.opsClock) {
      // do the zipping
      const inductEvent = viewModel.opsClockData.opsClock.clockEvents.find(
        (clockEvent) =>
          clockEvent.process === induct &&
          clockEvent.cycleName === DEFAULT_CYCLE,
      );
      const inductTimestamp = inductEvent
        ? inductEvent.timeIntervals[0].startTime
        : -1;

      if (inductTimestamp < 0) {
        return;
      }
      const inductTime = new Date(
        new Date(inductTimestamp * 1000).toLocaleString("en-US", {
          timeZone,
        }),
      );

      if (
        viewModel.triggerConfigurations &&
        viewModel.triggerConfigurations.length > 0
      ) {
        viewModel.triggerConfigurations.forEach((triggerConfig) => {
          if (triggerConfig.triggerTarget !== "PRE_SHIFT") {
            return;
          }
          const runTime = add(inductTime, {
            minutes: triggerConfig.autoRunOffsetInMinutes,
          });
          const approveTime = add(inductTime, {
            minutes: triggerConfig.autoApproveOffsetInMinutes,
          });
          const postTime = add(inductTime, {
            minutes: triggerConfig.autoPostOffsetInMinutes,
          });

          entries.push({
            id: `${triggerConfig.stationCode}_${DEFAULT_CYCLE}`,
            cycle: DEFAULT_CYCLE,
            autoRun: triggerConfig.autoRunOffsetInMinutes !== null,
            inductTime,
            runTime,
            approveTime,
            postTime,
            autoPost: triggerConfig.autoPostOffsetInMinutes !== null,
          });
        });
      }
      if (entries.length === 0 && viewModel.triggerConfigurations) {
        entries.push({
          id: `${getStationCode()}_${DEFAULT_CYCLE}`,
          cycle: DEFAULT_CYCLE,
          autoRun: false,
          inductTime,
          runTime: null,
          approveTime: null,
          postTime: null,
          autoPost: false,
        });
      }
    }
    if (entries[0] && isEqual(entries[0].postTime, entries[0].inductTime)) {
      setPostTimeError(true);
    } else {
      setPostTimeError(false);
    }
    innerDispatch({ type: "SET_DATA", payload: { data: entries } });
  }, [viewModel]);

  useEffect(() => {
    parseDataFromBackend();
  }, [viewModel]);

  const [state, innerDispatch] = useReducer(AutoProcessesReducer, {
    dirty: false,
    data: [],
  });
  const [postTimeError, setPostTimeError] = useState(false);

  const onCancel = useCallback(() => {
    parseDataFromBackend();
  }, [state]);

  const onSave = useCallback(async () => {
    // perform the inverse of what we do in the zipping of opsClock and triggerConfiguration
    // for each entry in the state.data, find the corresponding triggerConfiguration and update it with the new values
    const newConfigs = state.data.map((item) => {
      const runTimeDelta = differenceInMinutes(item.runTime, item.inductTime);
      const postTimeDelta = differenceInMinutes(item.postTime, item.inductTime);

      return {
        autoApproveOffsetInMinutes: item.autoPost ? postTimeDelta : null,
        autoPostOffsetInMinutes: item.autoPost ? postTimeDelta : null,
        autoRunOffsetInMinutes: item.autoRun ? runTimeDelta : null,
        stationCode: getStationCode(),
        triggerTarget: "PRE_SHIFT",
      };
    });

    const responses = await Promise.all(
      newConfigs.map(async (newConfig) => {
        return await updateTriggerConfiguration(newConfig);
      }),
    );

    if (responses.some((response) => response.status === "500")) {
      console.error("Error saving Shift Plan Automation data");
    } else {
      dispatch({
        type: "LOAD_TRIGGER_CONFIGURATIONS",
        data: { dispatcher: dispatch },
      });
    }
  }, [state]);

  const changeItem = useCallback((item, key, value) => {
    innerDispatch({
      type: "CHANGE_ITEM",
      payload: { id: item.id, [key]: value },
    });
  }, []);

  const changeTime = useCallback((item, key, value) => {
    let newTime = new Date(item[key]);
    const [hours, minutes] = value.split(":");
    newTime.setHours(hours);
    newTime.setMinutes(minutes);
    newTime.setSeconds(0);

    if (key === POST_TIME_KEY) {
      if (isEqual(newTime, item.inductTime)) {
        setPostTimeError(true);
        return;
      } else {
        setPostTimeError(false);
      }
    }

    if (isAfter(newTime, item.inductTime)) {
      newTime = add(newTime, { days: -1 });
    }

    if (isValid(newTime) && newTime instanceof Date) {
      innerDispatch({
        type: "CHANGE_ITEM",
        payload: { id: item.id, [key]: newTime },
      });
    }
  }, []);

  return (
    <Column spacing="small" className="autoProcesses">
      <Row
        spacingInset="small 400"
        backgroundColor="secondary"
        className="autoProcessesSaveButton"
        alignmentHorizontal="end"
      >
        <Row>
          <Button
            type="secondary"
            className="breaksFilterButton breaks"
            onClick={onCancel}
            disabled={!state.dirty || !isUserAuthorisedForSettingsEdit}
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            className="breaks"
            disabled={!state.dirty || !isUserAuthorisedForSettingsEdit}
          >
            Save
          </Button>
        </Row>
      </Row>
      <Row>
        <Table
          headerRows={1}
          showStripes={false}
          showDividers={true}
          spacing="small"
        >
          <TableRow>
            <TableCell>Cycle</TableCell>
            <TableCell>Auto Run</TableCell>
            <TableCell width={140}>Run Time</TableCell>
            <TableCell width={100}>Auto Post</TableCell>
            <TableCell width={140}>Post Time</TableCell>
            <TableCell width={120}>Induct Time</TableCell>
          </TableRow>
          {state.data.map((item, idx) => {
            return (
              <TableRow>
                <TableCell>{item.cycle}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={item.autoRun}
                    onChange={(value) => changeItem(item, RUN_TIME_KEY, value)}
                    disabled={!isUserAuthorisedForSettingsEdit}
                  />
                </TableCell>
                <TableCell>
                  <TimePicker
                    showSecond={false}
                    timeOnly={true}
                    minuteStep={15}
                    width={140}
                    value={
                      item.runTime ? format(item.runTime, TIME_FORMAT) : ""
                    }
                    placeholder="HH:MM"
                    format={{
                      hourCycle: "h23",
                      hour: "2-digit",
                      minute: "2-digit",
                    }}
                    disabled={!isUserAuthorisedForSettingsEdit}
                    onChange={(time) => changeTime(item, "runTime", time)}
                    focusOnOpen={true}
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    checked={item.autoPost}
                    onChange={(value) => changeItem(item, "autoPost", value)}
                    disabled={!isUserAuthorisedForSettingsEdit}
                  />
                </TableCell>
                <TableCell>
                  <TimePicker
                    showSecond={false}
                    timeOnly={true}
                    minuteStep={15}
                    width={140}
                    value={
                      item.postTime ? format(item.postTime, TIME_FORMAT) : ""
                    }
                    placeholder="HH:MM"
                    format={{
                      hourCycle: "h23",
                      hour: "2-digit",
                      minute: "2-digit",
                    }}
                    error={postTimeError}
                    errorMessage={postTimeError ? POST_TIME_ERROR : ""}
                    disabled={!isUserAuthorisedForSettingsEdit}
                    onChange={(time) => changeTime(item, POST_TIME_KEY, time)}
                    focusOnOpen={true}
                  />
                </TableCell>
                <TableCell>{format(item.inductTime, "HH:mm")}</TableCell>
              </TableRow>
            );
          })}
        </Table>
      </Row>
    </Column>
  );
};

export default ShiftPlanAutomation;



// WEBPACK FOOTER //
// ./src/components/settings/settingsv2/ShiftPlanAutomation.js